/**
 * Подразделы «Экологии» клиента-компании (ecology-sites, макет варианта Б 17.09.2026):
 * «Проектные работы» (`/ecology/projects`) — документы и нормативы по площадкам со сроком действия,
 * «Отчётность» (`/ecology/reports`) — отчёты и мероприятия всех площадок по ближайшему сроку,
 * «Лабораторный контроль» (`/ecology/lab`, ecology-lab-control) — замеры всех площадок по сроку.
 * Таблица, фильтры и модалки — общий `ItemsSection` (тот же компонент у команды в карточке клиента).
 */
import { useNavigate } from 'react-router-dom'
import type { EcoSection } from '@/lib/api'
import { ecologyClientApi } from '@/lib/api'
import { useEcoData } from '@/ecology/use-eco-data'
import { useStartEcoOrder } from '@/ecology/order/start-order'
import { ItemsSection } from '@/ecology/items-section'
import { EcoTrainingCard } from '@/ecology/training-card'
import { EcoStatusLegend } from '@/ecology/status-legend'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Alert } from '@/design-system/components/feedback/alert'

const HEADS: Record<EcoSection, { title: string; subtitle: string }> = {
  projects: {
    title: 'Проектные работы',
    subtitle: 'Проекты, нормативы и паспорта по площадкам: срок действия и статус',
  },
  reports: {
    title: 'Отчётность',
    subtitle: 'Отчёты и мероприятия по всем площадкам — по ближайшему сроку',
  },
  lab: {
    title: 'Лабораторный контроль',
    subtitle: 'Замеры на границе СЗЗ, на источниках выбросов и воды — по ближайшему сроку',
  },
}

function EcologyItemsPage({ section }: { section: EcoSection }) {
  const navigate = useNavigate()
  const order = useStartEcoOrder()
  const { overview, catalog, settings, loading, error, setOverview } = useEcoData(ecologyClientApi)
  return (
    <div>
      <PageHead title={HEADS[section].title} subtitle={HEADS[section].subtitle} />
      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !overview ? (
        <Panel>
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        </Panel>
      ) : (
        <>
          {order.error ? (
            <Alert variant="danger" className="mb-4">
              {order.error}
            </Alert>
          ) : null}
          {/* Обучение по экологической безопасности — по журналу обучения (ecology-followup). */}
          {section === 'projects' ? (
            <EcoTrainingCard training={overview.training} journalHref="/training" />
          ) : null}
          <ItemsSection
            section={section}
            api={ecologyClientApi}
            overview={overview}
            catalog={catalog}
            onChange={setOverview}
            onSurvey={(site) => navigate(`/ecology/survey?site=${site.id}`)}
            onOrder={order.start}
            onOpenOrder={(orderId) => navigate(`/orders/${orderId}`)}
          />
          <EcoStatusLegend settings={settings} />
        </>
      )}
    </div>
  )
}

export function EcologyProjectsPage() {
  return <EcologyItemsPage section="projects" />
}

export function EcologyReportsPage() {
  return <EcologyItemsPage section="reports" />
}

export function EcologyLabPage() {
  return <EcologyItemsPage section="lab" />
}
